import { Box, CssBaseline, useMediaQuery } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { Outlet, useLocation } from 'react-router-dom';
// import XIcon from '@mui/icons-material/X';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Header, PublicFooter } from './components';

const Main = styled('div')(({ theme }) => ({
    ...theme.typography.mainContent,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    minHeight: '10vh',
    marginLeft: 0
}));

const PublicLayout = () => {
    const theme = useTheme();
    const location = useLocation();
    const mainRef = useRef<HTMLDivElement>(null);
    const prevScrollRef = useRef<number>(0);
    const drawerOpenRef = useRef<boolean>(true);
    const lgUp = useMediaQuery(theme.breakpoints.up('lg'));
    const [drawerOpen, setDrawerOpen] = useState<boolean>(true);
    const [headerHeight, setHeaderHeight] = useState<number>(lgUp ? 113 : 124);
    const isCompetitionsMapRoute = location.pathname === '/public/competitions-map';
    const isAthleteDetailsRoute = location.pathname.startsWith('/public/athletes/');
    const isOfficialDetailsRoute = location.pathname.startsWith('/public/officials/');

    useEffect(() => {
        drawerOpenRef.current = drawerOpen;
    }, [drawerOpen]);

    const handleHeaderHeightChange = useCallback((height: number) => {
        setHeaderHeight((previousHeight) => (previousHeight === height ? previousHeight : height));
    }, []);

    useEffect(() => {
        const mainElement = mainRef.current;
        if (!mainElement) return;

        if (isCompetitionsMapRoute || isAthleteDetailsRoute || isOfficialDetailsRoute) {
            if (!drawerOpenRef.current) setDrawerOpen(true);
            return;
        }

        const handleScroll = () => {
            const scrollContainer = mainRef.current;
            if (!scrollContainer) return;

            const scrollableHeight = scrollContainer.scrollHeight - scrollContainer.clientHeight;
            const currentScroll = scrollContainer.scrollTop || 0;

            if (scrollableHeight <= 0) {
                if (!drawerOpenRef.current) setDrawerOpen(true);
                prevScrollRef.current = 0;
                return;
            }

            const viewportHeight = typeof window === 'undefined' ? 0 : window.innerHeight;
            const minScrollableHeightForHeaderToggle = Math.max(96, Math.round(viewportHeight * (lgUp ? 0.18 : 0.26)));

            if (scrollableHeight < minScrollableHeightForHeaderToggle) {
                if (!drawerOpenRef.current) setDrawerOpen(true);
                prevScrollRef.current = currentScroll;
                return;
            }

            const delta = currentScroll - prevScrollRef.current;
            const toggleThreshold = 10;
            const openNearTopThreshold = 24;

            if (currentScroll <= openNearTopThreshold) {
                if (!drawerOpenRef.current) setDrawerOpen(true);
            } else if (delta > toggleThreshold && drawerOpenRef.current) {
                setDrawerOpen(false);
            }

            prevScrollRef.current = currentScroll;
        };

        mainElement.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            mainElement.removeEventListener('scroll', handleScroll);
        };
    }, [
        isCompetitionsMapRoute,
        isAthleteDetailsRoute,
        isOfficialDetailsRoute,
        lgUp
    ]);

    const mainMarginTop = useMemo(() => `${headerHeight}px`, [headerHeight]);

    const mainHeight = useMemo(() => `calc(100vh - ${headerHeight}px)`, [headerHeight]);

    return (
        <Box sx={{ height: '100vh', overflow: 'hidden', overscrollBehaviorY: 'none', pb: 2 }}>
            <CssBaseline />
            <Header open={drawerOpen} onHeightChange={handleHeaderHeightChange} />
            <Main
                id="public-main-scroll-container"
                ref={mainRef}
                sx={{
                    background: theme.palette.background.default,
                    mt: mainMarginTop,
                    boxSizing: 'border-box',
                    overflowY: 'auto',
                    overscrollBehaviorY: 'contain',
                    height: mainHeight,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    width: lgUp ? '100%' : '94%'
                }}
                theme={theme}
            >
                <Outlet />
                <PublicFooter desktop={lgUp} />
            </Main>
        </Box>
    );
};

export default PublicLayout;
